import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFirebaseMultiplayer } from '../hooks/useFirebaseMultiplayer';
import { useCountry } from '../hooks/useCountry';
import { useAuthStore } from '../stores/useAuthStore';
import { useGuestStore } from '../stores/useGuestStore';
import { Loader2, AlertCircle, RefreshCw, Link, Share2, Ban } from 'lucide-react';
import ChatBox from './ChatBox';
import GameEndModal from './GameEndModal';
import VideoChat from './VideoChat';
import { getAvatarById } from './AvatarPicker';
import { GAMES, GameId } from '../games/registry';
import { useAppConfig } from '../hooks/useAppConfig';
import GameErrorBoundary from './GameErrorBoundary';
import { normalizeWinner } from '../utils/normalizeWinner';
import { calculateElo } from '../utils/elo';
import { firestore, doc, getDoc, updateDoc } from '../lib/firebase';
import { useSound } from '../hooks/useSound';

interface GameAreaProps {
  onGameEnd?: () => void;
}

export default function GameArea({ onGameEnd = () => {} }: GameAreaProps) {
  const [selectedGameId, setSelectedGameId] = useState<GameId>(null);
  const [showEndModal, setShowEndModal] = useState(false);
  const [gameWinner, setGameWinner] = useState<string | null>(null);
  const [opponentDisconnected, setOpponentDisconnected] = useState(false);
  const [waitSeconds, setWaitSeconds] = useState(0);
  const [copied, setCopied] = useState(false);
  const waitTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { play: playSound } = useSound();

  const [searchParams, setSearchParams] = useSearchParams();

  const { country } = useCountry();
  const authUser = useAuthStore(state => state.user);
  const guestUsername = useGuestStore(state => state.username);
  const { disabledGames } = useAppConfig();
  const visibleGames = GAMES.filter(g => !disabledGames.has(g.id));
  const isBanned = authUser?.isBanned === true;

  const currentUsername = authUser?.username || guestUsername;
  const userInfo = {
    username: currentUsername,
    avatar: authUser?.photoURL || undefined,
    authId: authUser?.uid || undefined,
  };

  const {
    isHost,
    gameId,
    isConnected,
    isOffline,
    lastError,
    isMatchmaking,
    opponentLeft,
    opponentCountry,
    opponentUsername,
    opponentAvatar,
    opponentAuthId,
    createGame,
    joinGameById,
    sendGameState,
    sendChat,
    leaveGame,
    messages,
    reconnect,
    playerId,
    rematchStatus,
    requestRematch
  } = useFirebaseMultiplayer(country, userInfo);

  // Handle invite link on load — ?invite=GAMEID&game=checkers
  useEffect(() => {
    const inviteId = searchParams.get('invite');
    const inviteGame = searchParams.get('game') as GameId;
    if (inviteId && inviteGame && (isConnected || isOffline)) {
      setSelectedGameId(inviteGame);
      joinGameById(inviteId, inviteGame);
      // Clean URL so refreshing doesn't re-join
      setSearchParams({}, { replace: true });
    }
  }, [isConnected, isOffline]);

  // Wait time tracker — starts when matchmaking begins, resets when done
  useEffect(() => {
    if (isMatchmaking) {
      setWaitSeconds(0);
      waitTimerRef.current = setInterval(() => setWaitSeconds(s => s + 1), 1000);
    } else {
      if (waitTimerRef.current) {
        clearInterval(waitTimerRef.current);
        if (gameId && selectedGameId) playSound('matchFound');
      }
      setWaitSeconds(0);
    }
    return () => { if (waitTimerRef.current) clearInterval(waitTimerRef.current); };
  }, [isMatchmaking]);

  // Show disconnect modal as soon as opponent leaves
  useEffect(() => {
    if (opponentLeft && selectedGameId) {
      setOpponentDisconnected(true);
      setShowEndModal(true);
    }
  }, [opponentLeft, selectedGameId]);

  // If the currently selected game is disabled by admin, drop the user back to the lobby
  useEffect(() => {
    if (selectedGameId && disabledGames.has(selectedGameId) && !gameId) {
      setSelectedGameId(null);
    }
  }, [selectedGameId, disabledGames, gameId]);

  const { storeGameResult } = useAuthStore();
  const handleGameEnd = async (winner: string | null) => {
    setGameWinner(winner);
    setShowEndModal(true);

    // Play sound
    if (winner === null) playSound('draw');
    else {
      const w = winner.toLowerCase();
      const iWon = (w === 'you') || (isHost && (w === 'x' || w === 'red' || w === 'host')) || (!isHost && (w === 'o' || w === 'black' || w === 'guest'));
      playSound(iWon ? 'win' : 'lose');
    }

    // Record result if both players are authenticated
    if (selectedGameId && authUser?.uid && opponentAuthId) {
      const result = normalizeWinner(winner, isHost, selectedGameId);
      if (result.shouldRecord) {
        const hostId = isHost ? authUser.uid : opponentAuthId;
        const guestId = isHost ? opponentAuthId : authUser.uid;
        const winnerId = result.winnerRole === 'host' ? hostId
          : result.winnerRole === 'guest' ? guestId
          : undefined;
        try {
          await storeGameResult(hostId, guestId, selectedGameId, winnerId, result.isDraw);

          // Update ELO ratings
          const [hostSnap, guestSnap] = await Promise.all([
            getDoc(doc(firestore, 'profiles', hostId)),
            getDoc(doc(firestore, 'profiles', guestId)),
          ]);

          if (hostSnap.exists() && guestSnap.exists()) {
            const eloResult = result.winnerRole === 'host' ? 1 : result.winnerRole === 'guest' ? 0 : 0.5;
            const [newHostElo, newGuestElo] = calculateElo(
              hostSnap.data()?.elo_rating ?? 1000,
              guestSnap.data()?.elo_rating ?? 1000,
              eloResult as 1 | 0 | 0.5
            );

            await Promise.all([
              updateDoc(doc(firestore, 'profiles', hostId), { elo_rating: newHostElo }),
              updateDoc(doc(firestore, 'profiles', guestId), { elo_rating: newGuestElo }),
            ]);
          }
        } catch {
          // Silently fail — don't disrupt the game end flow
        }
      }
    }
  };

  const handleMainMenu = () => {
    setSelectedGameId(null);
    setShowEndModal(false);
    setOpponentDisconnected(false);
    setGameWinner(null);
    onGameEnd();
  };

  const handleRequeue = async () => {
    setShowEndModal(false);
    setOpponentDisconnected(false);
    setGameWinner(null);
    if (selectedGameId) {
      await leaveGame();
      createGame(selectedGameId);
    }
  };

  const { sendFriendRequest: sendFriendRequestToSupabase } = useAuthStore();
  const handleFriendRequest = async () => {
    if (authUser?.uid && opponentAuthId) {
      await sendFriendRequestToSupabase(authUser.uid, opponentAuthId);
    }
  };

  const handleGameSelect = (id: GameId) => {
    setSelectedGameId(id);
    if ((isConnected || isOffline) && !isMatchmaking && !gameId && id) {
      createGame(id);
    }
  };

  const getInviteUrl = () =>
    `${window.location.origin}/play?invite=${gameId}&game=${selectedGameId}`;

  const handleShare = async () => {
    const url = getInviteUrl();
    const gameName = GAMES.find(g => g.id === selectedGameId)?.name ?? 'a game';
    const shareData = {
      title: '2Players — Play with me!',
      text: `Join me for a game of ${gameName} on 2Players!`,
      url,
    };

    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const renderError = () => (
    <div className="flex flex-col items-center justify-center p-8" role="alert">
      <div className="w-16 h-16 mb-6 text-red-500">
        <AlertCircle className="w-16 h-16" aria-hidden="true" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2">Connection Error</h3>
      {lastError && <p className="text-gray-300 mb-4 text-center">{lastError}</p>}
      {!isOffline && (
        <button
          onClick={reconnect}
          className="px-6 py-3 bg-white bg-opacity-10 rounded-lg hover:bg-opacity-20 transition flex items-center gap-2"
        >
          <RefreshCw className="w-5 h-5" aria-hidden="true" />
          Retry Connection
        </button>
      )}
    </div>
  );

  const renderSearching = () => (
    <div className="flex flex-col items-center justify-center p-8" role="status">
      <div className="w-16 h-16 mb-6">
        <Loader2 className="w-16 h-16 text-pink-500 animate-spin" aria-hidden="true" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2">Finding Opponent...</h3>
      <p className="text-gray-300">
        {waitSeconds < 20
          ? "This won't take long"
          : "Ok, it's taking a while.. why not invite someone to play? Or share it to your socials?"}
      </p>

      {/* Invite section — appears after 20 s of waiting */}
      {waitSeconds >= 20 && gameId && isHost && (
        <div className="mt-8 w-full max-w-sm text-center">
          <p className="text-gray-300 font-medium mb-3">
            Still no players? Invite a friend to play!
          </p>
          <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2 mb-3 text-left">
            <Link className="w-4 h-4 text-gray-300 shrink-0" />
            <span className="text-gray-300 text-sm truncate flex-1">{getInviteUrl()}</span>
          </div>
          <button
            onClick={handleShare}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-pink-600 to-violet-600 text-white rounded-lg font-semibold hover:opacity-90 transition"
          >
            <Share2 className="w-4 h-4" />
            {copied ? 'Link Copied!' : 'share' in navigator ? 'Share Invite Link' : 'Copy Invite Link'}
          </button>
        </div>
      )}

      <button
        onClick={() => setSelectedGameId(null)}
        className="mt-6 px-4 py-2 bg-white bg-opacity-10 rounded-lg hover:bg-opacity-20 transition"
      >
        Cancel
      </button>
    </div>
  );

  const renderBanned = () => (
    <div className="flex flex-col items-center justify-center p-8 text-center" role="alert">
      <Ban className="w-12 h-12 text-red-400 mb-4" />
      <h3 className="text-xl font-bold text-white mb-2">Your account is banned</h3>
      <p className="text-gray-300 mb-1">You can't start or join games.</p>
      {authUser?.banReason && (
        <p className="text-sm text-red-300">Reason: {authUser.banReason}</p>
      )}
    </div>
  );

  const renderGameSelection = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" role="menu">
      {visibleGames.length === 0 && (
        <div className="col-span-full text-center text-gray-300 py-8">
          No games are available right now. Please check back soon.
        </div>
      )}
      {visibleGames.map((game) => (
        <button
          key={game.id}
          onClick={() => handleGameSelect(game.id)}
          className="p-6 bg-white bg-opacity-5 rounded-xl hover:bg-opacity-10 transition group"
          role="menuitem"
          aria-label={`Play ${game.name}`}
        >
          <div className="flex flex-col items-center">
            <div
              className="w-16 h-16 rounded-lg flex items-center justify-center mb-4 text-3xl group-hover:scale-110 transition"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
            >
              {game.icon}
            </div>
            <h3 className="text-xl font-bold text-white">{game.name}</h3>
            <p className="text-gray-300 text-sm mt-2">{game.description}</p>
          </div>
        </button>
      ))}
    </div>
  );

  const renderGame = () => {
    if (isBanned) return renderBanned();
    if (!isOffline && !isConnected && lastError) return renderError();
    if (!selectedGameId) return renderGameSelection();
    if (!isOffline && (!gameId || isMatchmaking)) return renderSearching();

    const def = GAMES.find(g => g.id === selectedGameId);
    if (!def) return null;
    const GameComponent = def.component;

    return (
      <>
        {(opponentUsername || opponentCountry) && (
          <div className="flex items-center gap-2 text-gray-300 text-sm mb-3">
            {opponentAvatar && getAvatarById(opponentAvatar) && (
              <span className="text-lg">{getAvatarById(opponentAvatar)!.emoji}</span>
            )}
            <span>
              Playing against{' '}
              <span className="text-white font-medium">{opponentUsername || 'someone'}</span>
              {opponentCountry && ` from ${opponentCountry}`}
            </span>
          </div>
        )}
        <GameErrorBoundary gameName={def.name}>
          <GameComponent
            onGameEnd={handleGameEnd}
            isHost={isHost}
            sendGameState={sendGameState}
            gameId={gameId}
            isOffline={isOffline}
          />
        </GameErrorBoundary>
        {!isOffline && <ChatBox onSendMessage={sendChat} messages={messages} />}
        {!isOffline && gameId && (
          <VideoChat gameId={gameId} playerId={playerId} isHost={isHost} />
        )}
        <GameEndModal
          show={showEndModal}
          gameType={selectedGameId}
          winner={gameWinner}
          opponentDisconnected={opponentDisconnected}
          opponentUsername={opponentUsername}
          opponentAvatar={opponentAvatar}
          canSendFriendRequest={!!authUser?.uid && !!opponentAuthId}
          rematchStatus={rematchStatus}
          onRematch={requestRematch}
          onMainMenu={handleMainMenu}
          onRequeue={handleRequeue}
          onFriendRequest={handleFriendRequest}
        />
      </>
    );
  };

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-8 relative">
      {renderGame()}
    </div>
  );
}
